import { Loader2 } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  useAddPhoto,
  useProtocolPhotos,
  useRemovePhoto,
} from "@/hooks/useProtocol";
import { PhotoGrid } from "@/components/protocol/PhotoGrid";
import { getProtocolKeyCopy } from "@/lib/protocol-key-copy";
import type { ProtocolKind } from "@/lib/protocol";

export const MIN_KEY_PHOTOS = 1;

export function KeyPhotosStep({
  protocolId,
  kind,
}: {
  protocolId: string;
  kind: ProtocolKind;
}) {
  const { language, tDict } = useLanguage();
  const copy = getProtocolKeyCopy(language, tDict);
  const { data: photos = [], isLoading } = useProtocolPhotos(protocolId);
  const add = useAddPhoto(protocolId);
  const remove = useRemovePhoto(protocolId);
  const keyPhotos = photos.filter((photo) => photo.category === "keys");

  const title = kind === "move_in" ? copy.keyPickupTitle : copy.keyReturnTitle;
  const subtitle =
    kind === "move_in" ? copy.keyPickupSubtitle : copy.keyReturnSubtitle;

  return (
    <section aria-labelledby="protocol-key-photos-title">
      <div className="flex flex-col gap-1 md:flex-row md:items-end md:justify-between md:gap-6">
        <div>
          <h2
            id="protocol-key-photos-title"
            className="text-[26px] font-bold leading-tight text-neutral-900 md:text-3xl"
          >
            {title}
          </h2>
          <p className="mt-1 text-sm text-neutral-500 md:max-w-xl">
            {subtitle}
          </p>
        </div>
        <span className="text-sm font-semibold text-neutral-900">
          {keyPhotos.length} / {MIN_KEY_PHOTOS}
        </span>
      </div>

      <div className="mt-5 md:mt-8">
        {isLoading ? (
          <div className="flex items-center justify-center py-10 text-neutral-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : (
          <PhotoGrid
            photos={keyPhotos}
            category="keys"
            labels={copy}
            onAdd={(file, ordering) =>
              add.mutateAsync({ file, category: "keys", ordering })
            }
            onRemove={(photo) => remove.mutateAsync(photo)}
            uploading={add.isPending}
          />
        )}
      </div>
    </section>
  );
}

export function isKeyPhotosStepComplete(keyPhotosLength: number): boolean {
  return keyPhotosLength >= MIN_KEY_PHOTOS;
}
